import React, { memo, ReactElement } from 'react';
import { Icon, Tooltip } from '@dexma/ui-components';
import {
    StyledContent,
    StyledBar,
    StyledDetails,
    StyledValue,
    StyledOuterWrapper,
    StyledPinnedTooltip,
    StyledPreposition,
    StyledFlexContent,
    StyledFlexIcon,
    StyledCompareValue,
    StyledCompareDetails,
    StyledCompareWrapper,
    StyledComparedArrow,
} from 'adapter/in/common/components/widgets/SingleKPI/Regular/style';
import i18n from 'asset/translation';
import { handleUnitAndValue } from 'adapter/in/common/components/widgets/utils/handleUnitAndValue';
import { PeriodType } from 'domain/entity/widgetData';
import { SingleKPIComparison, SingleKPIWidgetData } from 'domain/entity/widgets/SingleKPIData';
import Branding from 'domain/entity/branding';
import { getColor, getPeriodFrom, getPeriodTo } from 'adapter/in/common/utils/widgetDataUtils';
import { applyTwoDecimals, removeDecimalsAndApplySeparator } from 'adapter/in/common/utils/numberFormatter';
import DataAvailability from 'adapter/in/common/components/widgets/DataAvailability';
import { isUnitCurrency } from 'adapter/in/common/components/widgets/utils/isUnitCurrency';
import { checkIfHasComparisonData, checkIfHasMainData } from 'adapter/in/common/components/widgets/common/checkIfHasData';
import { checkMemoChanges } from 'adapter/in/common/components/widgets/common/checkRedraw';

type SingleKPIProps = {
    data: SingleKPIWidgetData;
    isLoading: boolean;
    isOnEdit: boolean;
    width: number;
    height: number;
    branding: Branding;
    decimalSeparator: string;
    thousandSeparator: string;
    dateFormat: string;
    showDataAvailability: boolean;
    isCurrencyLastPosition: boolean;
};

type SizeCalculations = {
    iconSize: number;
    fontSize: number;
    valueMaxChars: number;
    titleMaxChars: number;
};

const getIsMinimalSingleKpi = (width: number, height: number) => width < 250 && height <= width;

const getSize = (width: number, height: number, isMinimalSingleKpi: boolean): SizeCalculations => {
    const iconRelation = 4;
    const fontSizeRelation = isMinimalSingleKpi ? 18 : 20;
    const titleMaxCharsRelation = 4;

    const applyCorrection = (property: number, unlimited: boolean) => {
        const proportion = height / width;
        const basicSize = width / property;

        return unlimited ? basicSize * Math.min(proportion) : basicSize * Math.min(proportion, 0.8);
    };

    const iconSize = applyCorrection(iconRelation, true);
    const fontSize = applyCorrection(fontSizeRelation, false);
    const valueMaxChars = Math.round(width / fontSize / 2);
    const titleMaxChars = Math.round(applyCorrection(titleMaxCharsRelation, true));

    return { iconSize, fontSize, valueMaxChars, titleMaxChars };
};

const getTitle = (title: string, maxChars: number): ReactElement =>
    title.length > maxChars ? <Tooltip title={title}>{`${title.slice(0, maxChars - 1)}...`} </Tooltip> : <span>{title} </span>;

const SingleKPIRegular = (props: SingleKPIProps): ReactElement => {
    const {
        data,
        width,
        height,
        thousandSeparator,
        decimalSeparator,
        isLoading,
        isOnEdit,
        branding,
        dateFormat,
        showDataAvailability,
        isCurrencyLastPosition,
    } = props;

    const dataAvailability = isLoading ? { rawAvailabilities: [], enabled: false } : data.dataAvailability;
    const icon = isLoading ? 'electricity' : data.icon.key;
    const color = isLoading ? branding.primaryColor : getColor(branding)(data.color);
    const from = isLoading ? 'last seven days' : getPeriodFrom(data.description.period, dateFormat);
    const to = isLoading ? ' ' : getPeriodTo(data.description.period, dateFormat);
    const hasComparison = isLoading ? false : data.comparison.enabled;
    const hasMainData = isLoading ? true : checkIfHasMainData(data.dataAvailability);
    const hasComparisonData = isLoading ? true : checkIfHasComparisonData(data.dataAvailability);
    const isMinimalSingleKpi = getIsMinimalSingleKpi(width, height);
    const shouldShowIcon = !isMinimalSingleKpi || (isMinimalSingleKpi && !hasComparisonData);
    const shouldShowDataAvailability = showDataAvailability && !isMinimalSingleKpi && !isLoading && !isOnEdit;
    const { iconSize, fontSize, valueMaxChars, titleMaxChars } = getSize(width, height, isMinimalSingleKpi);
    const title = isLoading ? <span>Sample SingleKPI</span> : getTitle(data.text, titleMaxChars);
    const value = isLoading ? '23' : data.value.toString();
    const units = isLoading ? 'kWh' : data.units;
    const isReversed = !isCurrencyLastPosition && isUnitCurrency(units);

    return (
        <StyledOuterWrapper $fontSize={fontSize} data-testid='single-KPI-widget' data-id='skpi'>
            <StyledBar data-testid='color-check' color={color} />
            {shouldShowDataAvailability && <DataAvailability dataAvailability={dataAvailability} />}
            <StyledContent data-testid='content' $fontSize={fontSize} $hasIcon={shouldShowIcon}>
                {shouldShowIcon && <IconComponent iconSize={iconSize} color={color} icon={icon} ariaLabel='' />}
                <StyledFlexContent>
                    <Value
                        value={value}
                        unit={units}
                        isReversed={isReversed}
                        valueMaxChars={valueMaxChars}
                        thousandSeparator={thousandSeparator}
                        decimalSeparator={decimalSeparator}
                        hasMainData={hasMainData}
                    />
                    <Details titleElement={title} from={from} to={to} />
                    {hasComparison && (
                        <Comparison
                            dateFormat={dateFormat}
                            isReversed={isReversed}
                            hasMainData={hasMainData}
                            hasComparisonData={hasComparisonData}
                            branding={branding}
                            thousandSeparator={thousandSeparator}
                            decimalSeparator={decimalSeparator}
                            units={units}
                            comparison={data.comparison}
                            value={data.value}
                        />
                    )}
                </StyledFlexContent>
            </StyledContent>
        </StyledOuterWrapper>
    );
};

type IconComponentPropsType = {
    icon: string;
    color: string;
    iconSize: number;
    ariaLabel: string;
};
const IconComponent = (props: IconComponentPropsType) => {
    const { icon, iconSize, color, ariaLabel } = props;

    return (
        <StyledFlexIcon data-testid='single-kpi-icon'>
            <Icon size={iconSize} name={icon} color={color} ariaLabel={ariaLabel} />
        </StyledFlexIcon>
    );
};

type ValuePropsType = {
    value: string;
    unit: string;
    isReversed: boolean;
    valueMaxChars: number;
    thousandSeparator: string;
    decimalSeparator: string;
    hasMainData: boolean;
};
const Value = (props: ValuePropsType) => {
    const { value, unit, isReversed, valueMaxChars, decimalSeparator, thousandSeparator, hasMainData } = props;

    const { formattedUnit, formattedValue } = handleUnitAndValue(
        unit,
        value,
        valueMaxChars,
        decimalSeparator,
        thousandSeparator,
        hasMainData
    );

    const content = isReversed ? (
        <>
            <span className='unit'>{formattedUnit}</span>&nbsp;<span>{formattedValue}</span>
        </>
    ) : (
        <>
            <span>{formattedValue}</span>&nbsp;
            <span className='unit'>{formattedUnit}</span>
        </>
    );

    return <StyledValue data-testid='value'>{content}</StyledValue>;
};

type DetailPropsType = {
    titleElement: ReactElement;
    to?: string;
    from: string;
    location?: string;
    isLocationPinned?: boolean;
    isDatePinned?: boolean;
};

const Details = (props: DetailPropsType) => {
    const { titleElement, to, from, location, isLocationPinned, isDatePinned } = props;
    return (
        <StyledDetails>
            <div className='details-content'>
                {titleElement}
                <StyledPreposition>{i18n.t('common.for')}</StyledPreposition> {!isDatePinned && from}{' '}
                {isDatePinned && !to && (
                    <StyledPinnedTooltip title={i18n.t('widgets.fixedDate')}>
                        <span data-testid='inside-date-tooltip'>{from}</span>
                    </StyledPinnedTooltip>
                )}
                {!isDatePinned && to && (
                    <>
                        <StyledPreposition>{i18n.t('common.to')}</StyledPreposition> <span>{to}</span>
                    </>
                )}
                {isDatePinned && to && (
                    <StyledPinnedTooltip data-testid='inside-fromto-tooltip' title={i18n.t('widgets.fixedDate')}>
                        <span data-testid='inside-date-combo-tooltip'>{from}</span>{' '}
                        <StyledPreposition>{i18n.t('common.to')}</StyledPreposition> <span>{to}</span>
                    </StyledPinnedTooltip>
                )}
                {location && isLocationPinned && (
                    <>
                        {' '}
                        <StyledPreposition>{i18n.t('common.in')}</StyledPreposition>{' '}
                        <StyledPinnedTooltip title={i18n.t('widgets.fixedLocation')}>
                            <span data-testid='inside-location-tooltip'>{location}</span>
                        </StyledPinnedTooltip>
                    </>
                )}
            </div>
        </StyledDetails>
    );
};

type ComparisonProps = {
    comparison: SingleKPIComparison;
    value: number;
    units: string;
    branding: Branding;
    dateFormat: string;
    thousandSeparator: string;
    decimalSeparator: string;
    hasMainData: boolean;
    isReversed: boolean;
    hasComparisonData: boolean;
};

const comparisonValueAsString = (value: number, thousandSeparator: string, decimalSeparator: string) => {
    if (Number.isNaN(value)) return '0';
    return value % 1 === 0 || value > 10000
        ? removeDecimalsAndApplySeparator(value, thousandSeparator)
        : applyTwoDecimals(value.toString(), decimalSeparator, thousandSeparator);
};

const enum COMPARISON_COLORS {
    POSITIVE = '#00CC87',
    NEGATIVE = '#F25A5A',
    NEUTRAL = '#141518',
}

const Comparison = (props: ComparisonProps) => {
    const {
        comparison,
        value,
        units,
        branding,
        dateFormat,
        thousandSeparator,
        decimalSeparator,
        hasMainData,
        hasComparisonData,
        isReversed,
    } = props;

    const color = getColor(branding)(comparison.color);
    const unit = comparison.showAsPercentage ? '%' : ` ${units}`;

    const valueAsPercentage = (regularValue: number, comparisonValue: number) =>
        Math.abs(((regularValue - comparisonValue) / Math.abs(comparisonValue)) * 100);
    const valueAsRelative = (regularValue: number, comparisonValue: number) => Math.abs(regularValue - comparisonValue);

    const getComparisonValue = (regularValue: number, comparisonValue: number, showAsPercentage: boolean): string =>
        hasMainData && hasComparisonData
            ? comparisonValueAsString(
                  showAsPercentage ? valueAsPercentage(regularValue, comparisonValue) : valueAsRelative(regularValue, comparisonValue),
                  thousandSeparator,
                  decimalSeparator
              )
            : '-';

    const getComparisonAriaLabel = () => {
        switch (color) {
            case COMPARISON_COLORS.POSITIVE:
                return i18n.t('widgets.widgetMenu.comparison.ariaLabel.betterValue');
            case COMPARISON_COLORS.NEGATIVE:
                return i18n.t('widgets.widgetMenu.comparison.ariaLabel.worseValue');
            default:
                return i18n.t('widgets.widgetMenu.comparison.ariaLabel.neutralValue');
        }
    };

    const getComparisonIcon = (): undefined | ReactElement => {
        if (hasMainData && hasComparisonData) {
            let icon = '=';
            if (value > comparison.value) icon = '↑';
            else if (value < comparison.value) icon = '↓';
            return (
                <StyledComparedArrow color={color} aria-label={getComparisonAriaLabel()}>
                    {icon}
                </StyledComparedArrow>
            );
        }
        return undefined;
    };

    return (
        <StyledCompareWrapper>
            <StyledCompareValue data-testid='compare' color={color}>
                {getComparisonIcon()}{' '}
                {isReversed
                    ? `${unit} ${getComparisonValue(value, comparison.value, comparison.showAsPercentage)} `
                    : `${getComparisonValue(value, comparison.value, comparison.showAsPercentage)} ${unit}`}
            </StyledCompareValue>
            <StyledCompareDetails>
                {i18n.t('common.comparedTo')}{' '}
                {`${getPeriodFrom(comparison.period, dateFormat)} ${
                    comparison.period.type === PeriodType.INTERVAL ? i18n.t('common.to') : ''
                } ${getPeriodTo(comparison.period, dateFormat)}`}
            </StyledCompareDetails>
        </StyledCompareWrapper>
    );
};

export default memo(SingleKPIRegular, checkMemoChanges);
